/**
 * useCampaignExecutor
 *
 * Single abstraction for executing one campaign sequence step on a lead.
 * Dispatches to:
 *   • Chrome extension  — when campaign.executionMethod === 'extension' (or unset)
 *   • Cloud proxy       — when campaign.executionMethod === 'proxy'
 *     (FE must not execute; ld-gcp-campaign-executor owns Unipile sends)
 *
 * When REACT_APP_SIM_HIDE_UI is NOT set the Chrome extension path is a no-op (DB-only dev sim).
 * Set REACT_APP_SIM_HIDE_UI=true to dispatch real extension → LinkedIn API calls.
 *
 * Usage:
 *   const executor = useCampaignExecutor();
 *   await executor.execute(campaign, lead, step, content);
 */

import { useCallback, useRef } from 'react';
import { ICampaign, ICampaignLead, ISequenceStep } from '../../../interfaces/models/campaign';
import { useExtensionRequest } from '../../../services/apis/extension/useExtensionRequest';
import { useCampaignsRequest } from '../../../services/apis/campaigns/useCampaignsRequest';
import { useAppActions } from '../../../stores/application/useAppActions';
import {
  collectOutboundDmTexts,
  conversationMessagesList,
  isInboundMessageReactionLike,
} from '../utils/campaignMessageReply';
import { buildLinkedInPostUrl } from '../utils/linkedinPostUrl';
import {
  isRealVanityPublicIdentifier,
  isSalesNavProfileId,
  parseSalesLeadAuth,
} from '../utils/salesNavListLeads';

// Random delay between 2 s and REACT_APP_SIM_BATCH_DELAY_MAX_S (default 8 s).
const BATCH_DELAY_MAX_MS =
  parseInt(process.env.REACT_APP_SIM_BATCH_DELAY_MAX_S ?? '8', 10) * 1000;
const batchDelay = () =>
  new Promise<void>(r =>
    setTimeout(r, 2000 + Math.round(Math.random() * Math.max(0, BATCH_DELAY_MAX_MS - 2000)))
  );

// True when controls are hidden — means this is a "real" run, not dev simulation.
const PRODUCTION_MODE = !!process.env.REACT_APP_SIM_HIDE_UI;

export interface IStepExecutionResult {
  success: boolean;
  reason?: string;
  /** Actual note/message text that was sent (after quota stripping). */
  sentContent?: string;
  hasNote?: boolean;
}

/**
 * Extracts the first post's action fields from the raw extension getProfilePosts response.
 * The extension returns a LinkedIn Voyager API response with an `included` array.
 * We find the first Update (post) and pull out postId (entityUrn) and ugcPostId.
 */
function extractLatestPost(lnPosts: any): { postId: string; ugcPostId: string; text?: string; postUrl?: string } | null {
  if (!lnPosts?.included) return null;

  const update = lnPosts.included.find(
    (item: any) => item.$type === 'com.linkedin.voyager.dash.feed.Update'
  );
  if (!update) return null;

  const postId: string = update.entityUrn ?? '';

  // Extract ugcPostId from *socialDetail (e.g. "...ugcPost:12345..." or "...activity:12345...")
  const socialDetail: string = update['*socialDetail'] ?? '';
  let ugcPostId = '';
  const ugcMatch = socialDetail.match(/ugcPost:(\d+)/);
  const activityMatch = socialDetail.match(/activity:(\d+)/);
  if (ugcMatch) ugcPostId = `urn:li:ugcPost:${ugcMatch[1]}`;
  else if (activityMatch) ugcPostId = `urn:li:activity:${activityMatch[1]}`;

  if (!postId) return null;

  let text = '';
  if (update.commentary?.text?.text) {
    text = update.commentary.text.text;
  }

  const postUrl = update.socialContent?.shareUrl
    || buildLinkedInPostUrl(ugcPostId)
    || buildLinkedInPostUrl(update.metadata?.shareUrn);

  return { postId, ugcPostId, text, postUrl: postUrl || undefined };
}

/**
 * Extracts LinkedIn network distance from a raw extension getProfileView response.
 * Voyager responses carry it as distance.value / memberDistance.value ('DISTANCE_1', …)
 * either at the top level or on an item in the `included` array.
 * Returns true (1st degree), false (not 1st), or null when it cannot be determined.
 */
function parseFirstDegree(rawView: any): boolean | null {
  // Extension responses arrive wrapped as { result: … }.
  const profileView = rawView?.result ?? rawView;

  // Preferred: the extension's profileInfo parser now reports `firstDegree` directly
  // (extracted from the fsd_memberRelationship entity of the FullProfile response).
  if (typeof profileView?.firstDegree === 'boolean') return profileView.firstDegree;

  // Fallback for raw Voyager responses: a fsd_memberRelationship entity in `included`
  // carries a union — `connection` (1st degree) or `noConnection.memberDistance`.
  for (const item of profileView?.included ?? []) {
    const rel = item?.memberRelationship;
    if (rel && typeof rel === 'object') {
      if (rel.connection || rel['*connection']) return true;
      if (rel.noConnection) {
        const distance = rel.noConnection.memberDistance;
        return distance ? String(distance).toUpperCase() === 'DISTANCE_1' : false;
      }
    }
  }

  const candidates: any[] = [
    profileView?.data?.distance?.value,
    profileView?.distance?.value,
    profileView?.data?.memberDistance?.value,
    profileView?.memberDistance?.value,
  ];
  for (const item of profileView?.included ?? []) {
    candidates.push(item?.distance?.value, item?.memberDistance?.value);
  }
  for (const c of candidates) {
    if (c === undefined || c === null) continue;
    const norm = String(c).toUpperCase();
    if (norm.startsWith('DISTANCE_') || norm.startsWith('FIRST') || /^\d$/.test(norm)) {
      return norm === 'DISTANCE_1' || norm === 'FIRST_DEGREE' || norm === '1';
    }
  }
  return null;
}

const IDENTITY_DASH_PROFILE_BY_VANITY_QUERY_ID = 'a3de77c32c473719f1c58fae6bff43a5';

/** LinkedIn member ids (ACoAA…) — not vanity slugs like "john-doe-123abc". */
function isMemberProfileId(id: string): boolean {
  const bare = id.replace(/^urn:li:fsd_profile:/, '').replace(/^urn:li:member:/, '');
  return /^ACo[A-Za-z0-9_-]{10,}$/i.test(bare);
}

/** Sales Navigator profile ids (ACw…) — not classic member ids. */
function isSalesNavMemberId(id: string): boolean {
  const bare = id.replace(/^urn:li:fsd_profile:/, '').replace(/^urn:li:member:/, '');
  return /^ACw[A-Za-z0-9_-]{10,}$/i.test(bare);
}

/** Ensure linkedinId is in urn:li:fsd_profile:XXX format for getProfilePosts. */
function toFsdProfileUrn(linkedinId: string): string {
  if (!linkedinId) return '';
  if (linkedinId.startsWith('urn:li:fsd_profile:')) return linkedinId;
  if (linkedinId.startsWith('urn:li:member:')) return linkedinId.replace('urn:li:member:', 'urn:li:fsd_profile:');
  return `urn:li:fsd_profile:${linkedinId}`;
}

function extensionActionOk(result: unknown): boolean {
  if (result instanceof Error || result === false) return false;
  const wrapped = result as { result?: unknown; error?: string };
  if (wrapped?.error) return false;
  const inner = wrapped?.result !== undefined ? wrapped.result : result;
  if (inner === false) return false;
  if (inner && typeof inner === 'object') {
    if ('success' in inner) return (inner as { success?: boolean }).success === true;
    if ('data' in inner || 'included' in inner || 'elements' in inner) return true;
    return typeof inner === 'object' && inner !== null;
  }
  return !!inner;
}

/** Pulls a human-readable error reason out of a failed extension call result. */
function extensionActionError(result: unknown): string | null {
  if (result instanceof Error) return result.message || 'extension_error';
  const wrapped = result as { error?: string; result?: { error?: string } };
  return wrapped?.error || wrapped?.result?.error || null;
}

/** LinkedIn free-tier cap on invitations WITH a personalized note (CUSTOM_INVITE_LIMIT_REACHED). */
function isCustomInviteLimitError(result: unknown): boolean {
  const err = extensionActionError(result) || '';
  return /CUSTOM_INVITE_LIMIT_REACHED/i.test(err);
}

/** Voyager GraphQL feed response — null/403/errors must not be treated as success. */
function getProfilePostsFetchError(rawPosts: any): string | null {
  if (rawPosts === false || rawPosts == null) return 'profile_posts_fetch_failed';
  const errors = rawPosts?.data?.errors ?? rawPosts?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors[0]?.message || 'profile_posts_unavailable';
  }
  if (!rawPosts?.included?.length) return 'no_posts_found';
  return null;
}

/**
 * Pick the Profile that matches the requested vanity — never fall back to an
 * unrelated Profile in `included` (mutuals / "People also viewed" / viewer).
 * That fallback caused invites to go to the wrong person with the right note.
 */
function profileFromIdentityDash(response: any, vanity: string): any | null {
  const included = response?.result?.included ?? response?.included;
  if (!Array.isArray(included)) return null;
  const want = String(vanity || '').trim().toLowerCase();
  if (!want) return null;

  const profiles = included.filter(
    (item: any) => item?.$type === 'com.linkedin.voyager.dash.identity.profile.Profile',
  );
  const exact = profiles.find(
    (item: any) => String(item?.publicIdentifier || '').trim().toLowerCase() === want,
  );
  if (exact) return exact;

  // Some responses omit publicIdentifier on the primary profile but still key
  // the GraphQL entity by vanity — accept only if a single Profile is present.
  if (profiles.length === 1 && !profiles[0]?.publicIdentifier) {
    return profiles[0];
  }
  return null;
}

function profileUrnFromIdentityDash(response: any, vanity: string): string | null {
  const profile = profileFromIdentityDash(response, vanity);
  const entityUrn: string | undefined = profile?.entityUrn;
  if (!entityUrn) return null;
  return entityUrn.startsWith('urn:') ? entityUrn : `urn:li:fsd_profile:${entityUrn}`;
}

function publicIdentifierFromIdentityDash(response: any, vanity: string): string | null {
  const id = profileFromIdentityDash(response, vanity)?.publicIdentifier;
  return typeof id === 'string' && id.trim() ? id.trim() : null;
}

function isVanitySlug(value: string): boolean {
  const bare = value.replace(/^urn:li:fsd_profile:/, '').replace(/^urn:li:member:/, '').trim();
  return !!bare && !isMemberProfileId(bare) && !isSalesNavMemberId(bare);
}

function extensionResultPayload(result: unknown): any {
  if (result instanceof Error || result === false || result == null) return null;
  const wrapped = result as { result?: unknown };
  return wrapped?.result !== undefined ? wrapped.result : result;
}

export function useCampaignExecutor() {
  const {
    sendConnectionRequest, createOrSendMessage,
    likeProfilePosts, commentProfilePosts, visitLinkedInProfile,
    getLoggedInLIUser, getProfilePosts, getProfileView, getProfileByIdentityDash,
    getConversationsFromParticipants, getMessagesFromConversation,
    getSalesNavigatorProfile, extractLinkedinProfileHtml,
  } = useExtensionRequest();
  const {
    checkExtensionStepLimit, checkConnectionRequestNote, checkLeadConnection, updateLeadIdentity,
  } = useCampaignsRequest();
  const { setToast } = useAppActions();

  // Cache the sender's entityUrn for message actions — fetched once from extension per hook instance.
  const senderUrnRef = useRef<string>('');
  // Cache resolved member URNs (vanity slug → urn:li:fsd_profile:ACoAA…) per lead.
  const memberUrnCacheRef = useRef<Map<string, string>>(new Map());
  // Cache fetched posts per lead URN to avoid double-fetching during AI generation + execution.
  const postsCacheRef = useRef<Map<string, any>>(new Map());
  // Throttle reply checks — at most once per lead per interval.
  const replyCheckTsRef = useRef<Map<string, number>>(new Map());
  // conversationId returned when we sent the campaign DM — avoids vanity-based lookup.
  const conversationIdCacheRef = useRef<Map<string, string>>(new Map());
  const REPLY_CHECK_INTERVAL_MS = 3 * 60 * 1000;
  /** Minimum wait after our DM before checking — avoids race with thread sync. */
  const REPLY_MIN_DELAY_MS = 2 * 60 * 1000;
  /** Small clock-skew tolerance when comparing LinkedIn timestamps to messageSentAt. */
  const REPLY_CLOCK_SKEW_MS = 30 * 1000;

  const normalizeMessageTimestamp = (value: unknown): number | null => {
    if (value == null) return null;
    if (typeof value === 'number' && Number.isFinite(value)) {
      // LinkedIn may return seconds; campaign timestamps are always ms.
      return value > 0 && value < 1e12 ? value * 1000 : value;
    }
    const parsed = new Date(value as string | number).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  };

  /**
   * SN list leads start with ACw linkedinId and no vanity. Resolve classic identity via
   * salesApiProfiles (flagship URL) + profile HTML (ACo), then persist on CampaignLead.
   */
  const ensureClassicIdentity = useCallback(
    async (campaignId: string, lead: ICampaignLead): Promise<ICampaignLead | { error: string }> => {
      if (isRealVanityPublicIdentifier(lead.publicIdentifier) || isMemberProfileId(lead.linkedinId || '')) {
        return lead;
      }
      if (!isSalesNavProfileId(lead.linkedinId) && lead.source !== 'sn_lists') {
        return lead;
      }

      const snProfileId = isSalesNavProfileId(lead.linkedinId) ? lead.linkedinId : '';
      let vanity = isRealVanityPublicIdentifier(lead.publicIdentifier) ? lead.publicIdentifier.trim() : '';
      let profileId = '';

      const auth = parseSalesLeadAuth(lead.linkedinUrl);
      if (auth?.snProfileId && auth.authType && auth.authToken) {
        try {
          const snRaw: any = await getSalesNavigatorProfile({
            SNprofileId: auth.snProfileId,
            authType: auth.authType,
            authToken: auth.authToken,
          });
          const sn = extensionResultPayload(snRaw) || snRaw;
          const fromSn = String(sn?.publicIdentifier || '').trim();
          if (isRealVanityPublicIdentifier(fromSn)) vanity = fromSn;
        } catch (err: any) {
          console.warn('[CampaignExecutor] getSalesNavigatorProfile failed:', err?.message);
        }
      }

      if (vanity) {
        try {
          const htmlRaw: any = await extractLinkedinProfileHtml({
            pageUrl: `https://www.linkedin.com/in/${vanity}/`,
            includeHtml: false,
          });
          const extracted = extensionResultPayload(htmlRaw) || htmlRaw;
          const htmlVanity = String(extracted?.publicIdentifier || '').trim();
          const htmlPid = String(extracted?.profileId || '').trim();
          if (isRealVanityPublicIdentifier(htmlVanity)) vanity = htmlVanity;
          if (isMemberProfileId(htmlPid)) profileId = htmlPid;
        } catch (err: any) {
          console.warn('[CampaignExecutor] extractLinkedinProfileHtml failed:', err?.message);
        }
      }

      if (!vanity && !profileId) {
        return { error: 'sn_identity_resolve_failed' };
      }

      try {
        const data = await updateLeadIdentity(campaignId, lead._id, {
          ...(vanity ? { publicIdentifier: vanity } : {}),
          ...(profileId ? { linkedinId: profileId } : {}),
          ...(snProfileId ? { snProfileId } : {}),
        });
        if (data?.lead) return data.lead as ICampaignLead;
      } catch (err: any) {
        console.warn('[CampaignExecutor] updateLeadIdentity failed:', err?.message);
      }

      return {
        ...lead,
        ...(vanity ? { publicIdentifier: vanity, linkedinUrl: `https://www.linkedin.com/in/${vanity}` } : {}),
        ...(profileId ? { linkedinId: profileId } : {}),
      };
    },
    [getSalesNavigatorProfile, extractLinkedinProfileHtml, updateLeadIdentity],
  );

  /**
   * CSV leads often store a vanity slug in linkedinId, but the Voyager APIs
   * (getProfilePosts, getProfileView, invitations, messaging) need the real
   * member id (ACoAA…) — resolve via identity dash when necessary.
   */
  const resolveMemberProfileUrn = useCallback(
    async (lead: ICampaignLead): Promise<{ urn: string } | { error: string }> => {
      const rawId = (lead.linkedinId || lead.publicIdentifier || '').trim();
      if (!rawId) return { error: 'missing_linkedin_id' };

      const bareId = rawId.replace(/^urn:li:fsd_profile:/, '').replace(/^urn:li:member:/, '');
      if (isMemberProfileId(bareId)) return { urn: toFsdProfileUrn(bareId) };
      if (isSalesNavMemberId(bareId) && !isRealVanityPublicIdentifier(lead.publicIdentifier)) {
        return { error: 'sn_identity_unresolved' };
      }

      const cached = memberUrnCacheRef.current.get(rawId);
      if (cached) return { urn: cached };

      const vanity = (isRealVanityPublicIdentifier(lead.publicIdentifier)
        ? lead.publicIdentifier
        : (isVanitySlug(bareId) ? bareId : '')
      ).trim();
      if (!vanity) return { error: 'missing_linkedin_id' };

      try {
        const response: any = await getProfileByIdentityDash({
          vanityName: vanity,
          identityDashProfileByVanityQueryId: IDENTITY_DASH_PROFILE_BY_VANITY_QUERY_ID,
        });
        if (response?.error) return { error: 'profile_lookup_failed' };
        const urn = profileUrnFromIdentityDash(response, vanity);
        if (!urn) return { error: 'profile_urn_not_found' };
        memberUrnCacheRef.current.set(rawId, urn);
        return { urn };
      } catch (err: any) {
        console.warn('[CampaignExecutor] profile URN lookup failed:', err?.message);
        return { error: 'profile_lookup_failed' };
      }
    },
    [getProfileByIdentityDash]
  );

  /**
   * Is the lead a 1st-degree connection? Primary source: the extension's
   * getProfileView (live LinkedIn data). Fallback: LeadDelta Contacts via the
   * backend connection-check ('LinkedIn 1st' auto-tag). Returns null = unknown.
   *
   * `memberUrn` must be a resolved member URN (ACoAA…) — getProfileView builds
   * `urn:li:fsd_profile:<id>` itself, so it receives the bare id, never a vanity slug.
   */
  const resolveFirstDegree = useCallback(
    async (campaignId: string, lead: ICampaignLead, memberUrn?: string): Promise<boolean | null> => {
      const bareMemberId = memberUrn?.replace(/^urn:li:fsd_profile:/, '');
      if (bareMemberId && isMemberProfileId(bareMemberId)) {
        try {
          const view: any = await getProfileView({ profileId: bareMemberId });
          const fromExtension = parseFirstDegree(view);
          if (fromExtension !== null) return fromExtension;
        } catch (err: any) {
          console.warn('[CampaignExecutor] getProfileView failed, falling back to Contacts:', err?.message);
        }
      }
      try {
        const data = await checkLeadConnection(campaignId, lead._id);
        if (typeof data?.isFirstDegree === 'boolean') return data.isFirstDegree;
      } catch (err: any) {
        console.warn('[CampaignExecutor] Contacts connection-check failed:', err?.message);
      }
      return null;
    },
    [getProfileView, checkLeadConnection]
  );

  const fetchProfilePostsCached = useCallback(async (urn: string) => {
    if (postsCacheRef.current.has(urn)) {
      return postsCacheRef.current.get(urn);
    }
    const rawPosts = await getProfilePosts(urn);
    postsCacheRef.current.set(urn, rawPosts);
    return rawPosts;
  }, [getProfilePosts]);

  /**
   * Fetches the latest post for a lead. Used by simulation to get post text for AI comments.
   */
  const fetchLeadLatestPost = useCallback(
    async (lead: ICampaignLead): Promise<{ postId: string; ugcPostId: string; text?: string; postUrl?: string } | { error: string }> => {
      const urnResult = await resolveMemberProfileUrn(lead);
      if ('error' in urnResult) return { error: urnResult.error };

      const rawPosts = await fetchProfilePostsCached(urnResult.urn);
      const postsError = getProfilePostsFetchError(rawPosts);
      if (postsError) return { error: postsError };

      const post = extractLatestPost(rawPosts);
      if (!post) return { error: 'no_posts_found' };

      return post;
    },
    [resolveMemberProfileUrn, fetchProfilePostsCached]
  );

  /**
   * Public check: has the lead accepted our invite (i.e. become a 1st-degree
   * connection)? Resolves the member URN first so getProfileView never receives
   * a vanity slug. Returns true / false / null (unknown).
   */
  const checkLeadFirstDegree = useCallback(
    async (campaignId: string, lead: ICampaignLead): Promise<boolean | null> => {
      const urnResult = await resolveMemberProfileUrn(lead);
      const urn = 'urn' in urnResult ? urnResult.urn : undefined;
      return resolveFirstDegree(campaignId, lead, urn);
    },
    [resolveMemberProfileUrn, resolveFirstDegree]
  );

  const resolveConversationId = useCallback(
    async (lead: ICampaignLead): Promise<string | null> => {
      const cached = conversationIdCacheRef.current.get(lead._id);
      if (cached) return cached;

      if (!senderUrnRef.current) {
        const liUser: any = await getLoggedInLIUser();
        const urn: string = liUser?.entityUrn ?? liUser?.miniProfile?.entityUrn ?? '';
        if (urn) senderUrnRef.current = urn.startsWith('urn:li:') ? urn : `urn:li:fsd_profile:${urn}`;
      }
      if (!senderUrnRef.current) return null;

      const vanities = new Set<string>();
      const addVanity = (value?: string | null) => {
        const s = (value || '').trim();
        if (isVanitySlug(s)) vanities.add(s);
      };
      addVanity(lead.publicIdentifier);
      addVanity(lead.linkedinId);

      const lookupVanity = (lead.publicIdentifier || lead.linkedinId || '').trim();
      if (lookupVanity && isVanitySlug(lookupVanity)) {
        try {
          const response: any = await getProfileByIdentityDash({
            vanityName: lookupVanity,
            identityDashProfileByVanityQueryId: IDENTITY_DASH_PROFILE_BY_VANITY_QUERY_ID,
          });
          addVanity(publicIdentifierFromIdentityDash(response, lookupVanity));
        } catch { /* ignore — try stored vanities */ }
      }

      for (const vanity of Array.from(vanities)) {
        const convRaw: any = await getConversationsFromParticipants(vanity, senderUrnRef.current);
        const conversationId = convRaw?.conversationId ?? convRaw?.result?.conversationId;
        if (conversationId) {
          conversationIdCacheRef.current.set(lead._id, conversationId);
          return conversationId;
        }
      }
      return null;
    },
    [getLoggedInLIUser, getConversationsFromParticipants, getProfileByIdentityDash],
  );

  /**
   * Checks whether the lead sent an inbound LinkedIn message after our campaign DM
   * or invite note. Returns the reply text or null. Throttled per lead to avoid
   * hammering LN APIs; `force` skips the throttle (used right before sending a DM).
   */
  const checkLeadMessageReply = useCallback(
    async (lead: ICampaignLead, sinceMs: number, opts?: { force?: boolean }): Promise<{ text: string } | null> => {
      if (!PRODUCTION_MODE) return null;

      const lastCheck = replyCheckTsRef.current.get(lead._id) ?? 0;
      if (!opts?.force && Date.now() - lastCheck < REPLY_CHECK_INTERVAL_MS) return null;
      replyCheckTsRef.current.set(lead._id, Date.now());

      if (!sinceMs) return null;
      if (Date.now() - sinceMs < REPLY_MIN_DELAY_MS) return null;

      try {
        const conversationId = await resolveConversationId(lead);
        if (!conversationId) {
          const label = lead.publicIdentifier || lead.name || lead._id;
          console.warn('[CampaignExecutor] reply check: no conversation for', label);
          return null;
        }

        const msgRaw: any = await getMessagesFromConversation(conversationId);
        if (msgRaw === false) {
          console.warn('[CampaignExecutor] reply check: failed to load messages for', conversationId);
          return null;
        }
        const list = conversationMessagesList(msgRaw);
        if (!list.length) return null;

        const ourOutboundTexts = collectOutboundDmTexts(lead, list);

        // Anchor to our last outbound message — only count inbound after that.
        let anchorMs = sinceMs - REPLY_CLOCK_SKEW_MS;
        for (const msg of list) {
          if (msg.isSent !== true) continue;
          const ts = normalizeMessageTimestamp(msg.dateTimestamp);
          if (ts !== null && ts >= sinceMs - REPLY_CLOCK_SKEW_MS) {
            anchorMs = Math.max(anchorMs, ts);
          }
        }

        for (let i = list.length - 1; i >= 0; i--) {
          const msg = list[i];
          const text = (msg.text ?? msg.messageContent?.text ?? '').trim();
          if (!text) continue;
          // Skip our own outbound rows — even when LinkedIn mis-flags isSent.
          if (msg.isSent === true || isInboundMessageReactionLike(msg, ourOutboundTexts)) continue;
          const ts = normalizeMessageTimestamp(msg.dateTimestamp);
          if (ts === null || ts <= anchorMs) continue;
          return { text };
        }
      } catch (err: any) {
        console.warn('[CampaignExecutor] reply check failed:', err?.message);
      }
      return null;
    },
    [resolveConversationId, getMessagesFromConversation]
  );

  /**
   * Execute a single step for a lead.
   * Returns { success } — if false, caller can choose to skip the DB status update.
   */
  const executeInternal = useCallback(
    async (
      campaign: ICampaign,
      lead: ICampaignLead,
      step: ISequenceStep,
      content: string
    ): Promise<IStepExecutionResult> => {
      const method = campaign.executionMethod ?? 'extension';

      /**
       * Email steps belong to the cloud executor, even in an extension campaign —
       * the browser cannot send mail. This must return WITHOUT advancing the lead or
       * recording a failure: the cloud picks the same lead up on its next run, and a
       * failure here would burn its retry budget for a step this side never owned.
       *
       * The cloud enforces the same split from its end by filtering on the step index
       * in its lead query, so the two executors can never both act on one lead.
       */
      if (step.type === 'email') {
        return { success: false, reason: 'email_handled_by_cloud_executor' };
      }

      try {
        if (method === 'proxy') {
          // Proxy campaigns are handled by the GCP cloud executor (runs every minute).
          // The FE must not trigger execution — return a non-blocking skip result.
          return { success: false, reason: 'proxy_handled_by_cloud_executor' };

        } else {
          // Dev / simulation mode — skip browser actions, just return success.
          if (!PRODUCTION_MODE) return { success: true };

          // ── Daily + weekly limit check (extension) ─────────────────────────
          const limitCheck = await checkExtensionStepLimit(campaign._id, step.type);
          if (limitCheck?.allowed === false) {
            const reason = limitCheck?.denyReason === 'weekly_limit_reached'
              ? 'weekly_limit_reached'
              : 'daily_limit_reached';
            console.warn(`[CampaignExecutor] ${reason} for step type '${step.type}' — skipping`);
            return { success: false, reason };
          }

          // ── Chrome extension path ─────────────────────────────────────────
          let workingLead = lead;
          if (lead.source === 'sn_lists' || isSalesNavProfileId(lead.linkedinId)) {
            const resolved = await ensureClassicIdentity(campaign._id, lead);
            if ('error' in resolved) {
              return { success: false, reason: resolved.error };
            }
            workingLead = resolved;
          }

          switch (step.type) {
            case 'connection_request': {
              const vanity = (
                workingLead.publicIdentifier
                || (isVanitySlug(workingLead.linkedinId || '') ? workingLead.linkedinId : '')
                || ''
              ).trim();
              const urnResult = await resolveMemberProfileUrn(workingLead);
              const memberUrn = 'urn' in urnResult ? urnResult.urn : undefined;

              // Need at least one stable target. Prefer failing closed over inviting
              // a random Profile from a partial identity-dash response.
              if (!vanity && !memberUrn) {
                return {
                  success: false,
                  reason: 'error' in urnResult ? urnResult.error : 'missing_linkedin_id',
                };
              }

              if (memberUrn) {
                const firstDegree = await resolveFirstDegree(campaign._id, workingLead, memberUrn);
                if (firstDegree === true) {
                  console.log(`[CampaignExecutor] lead ${workingLead._id} already connected — skipping invite`);
                  return { success: true, reason: 'already_connected' };
                }
              }

              // Resolve personalized note (template/AI/override), then enforce monthly free-tier quota.
              let note = String(content || '').trim().slice(0, 300);
              let hasNote = false;
              if (note) {
                let isPremium: boolean | undefined;
                try {
                  const li: any = await getLoggedInLIUser();
                  if (typeof li?.isPremium === 'boolean') isPremium = li.isPremium;
                } catch { /* fail closed below */ }

                const noteCheck: any = await checkConnectionRequestNote(campaign._id, {
                  noteContent: note,
                  isPremium,
                });
                if (noteCheck?.wantsNote && noteCheck?.noteAllowed === false) {
                  const fallback = step.noteLimitFallback || 'without_note';
                  if (fallback === 'pause_noted') {
                    return { success: false, reason: 'note_quota_pause_noted' };
                  }
                  if (fallback === 'pause_campaign') {
                    return { success: false, reason: 'note_quota_pause_campaign' };
                  }
                  // Default: continue without note — never exceed LinkedIn's free monthly cap.
                  console.warn('[CampaignExecutor] monthly CR note quota reached — sending without note');
                  note = '';
                } else if (noteCheck?.hasNote) {
                  note = String(noteCheck.noteContent || note).slice(0, 300);
                  hasNote = true;
                } else if (isPremium === true) {
                  hasNote = true;
                } else {
                  // Fail closed if check failed unexpectedly
                  note = '';
                }
              }

              // Prefer vanity/publicIdentifier for the invite — extension re-resolves
              // the member URN from LinkedIn before sending (safer than a pre-parsed URN).
              const inviteTarget = vanity || memberUrn!;
              let inviteResult = await sendConnectionRequest(inviteTarget, note);

              // LinkedIn's own personalized-invite monthly cap (often already hit outside LD).
              // Sync our ledger, toast once, then honor the campaign's noteLimitFallback.
              if (!extensionActionOk(inviteResult) && hasNote && isCustomInviteLimitError(inviteResult)) {
                console.warn('[CampaignExecutor] LinkedIn CUSTOM_INVITE_LIMIT_REACHED — syncing note quota');
                try {
                  await checkConnectionRequestNote(campaign._id, {
                    noteContent: note,
                    isPremium: false,
                    markLinkedInNotesExhausted: true,
                  });
                } catch { /* best-effort ledger sync */ }

                setToast({
                  type: 'warning',
                  message:
                    'LinkedIn personalized invite limit reached (often from invites outside LeadDelta). '
                    + 'Further connection requests will follow your note fallback setting.',
                });

                const fallback = step.noteLimitFallback || 'without_note';
                if (fallback === 'pause_noted') {
                  return { success: false, reason: 'note_quota_pause_noted' };
                }
                if (fallback === 'pause_campaign') {
                  return { success: false, reason: 'note_quota_pause_campaign' };
                }

                // Default without_note: retry this invite once without a note.
                console.warn('[CampaignExecutor] retrying connection request without note after LinkedIn note limit');
                note = '';
                hasNote = false;
                inviteResult = await sendConnectionRequest(inviteTarget, '');
              }

              if (!extensionActionOk(inviteResult)) {
                const sendErr = extensionActionError(inviteResult) || 'connection_request_failed';
                // Without a note, CUSTOM_INVITE is unexpected — still stop the retry storm.
                if (isCustomInviteLimitError(inviteResult)) {
                  setToast({
                    type: 'warning',
                    message:
                      'LinkedIn rejected further connection invites with a personalized note. '
                      + 'Weekly invite activity may also be constrained — try again later.',
                  });
                  return { success: false, reason: 'linkedin_custom_invite_limit' };
                }
                return { success: false, reason: sendErr };
              }
              return {
                success: true,
                sentContent: hasNote ? note : 'Connection request sent',
                hasNote,
              };
            }

            case 'message': {
              const urnResult = await resolveMemberProfileUrn(workingLead);
              if ('error' in urnResult) {
                return { success: false, reason: urnResult.error };
              }

              // A direct message requires a 1st-degree connection. If an earlier step
              // sent a connection request, only send once acceptance is positively
              // confirmed — otherwise LinkedIn rejects the message with HTTP 422
              // (RECIPIENT_NOT_FIRST_DEGREE_CONNECTION). When no invite precedes this
              // step (e.g. messaging existing connections) an unknown status proceeds
              // optimistically, preserving the previous behaviour.
              const myStepIdx = workingLead.currentStepIndex ?? 0;
              const invitePrecedes = (campaign.sequence ?? [])
                .slice(0, myStepIdx)
                .some(s => s.type === 'connection_request');
              const firstDegree = await resolveFirstDegree(campaign._id, workingLead, urnResult.urn);
              if (firstDegree === false || (firstDegree !== true && invitePrecedes)) {
                console.warn(`[CampaignExecutor] lead ${workingLead._id} not a confirmed 1st-degree connection — postponing message step`);
                return { success: false, reason: 'connection_not_accepted' };
              }

              // Resolve sender URN from extension session (cached after first call).
              if (!senderUrnRef.current) {
                const liUser: any = await getLoggedInLIUser();
                const urn: string = liUser?.entityUrn ?? liUser?.miniProfile?.entityUrn ?? '';
                if (urn) {
                  senderUrnRef.current = urn.startsWith('urn:li:') ? urn : `urn:li:fsd_profile:${urn}`;
                }
              }
              if (!senderUrnRef.current) {
                return { success: false, reason: 'sender_urn_unavailable' };
              }
              const messageResult = await createOrSendMessage({
                profileId: senderUrnRef.current,
                participantUrns: [urnResult.urn],
                text: content,
              }, { all: true });
              if (!extensionActionOk(messageResult)) {
                const sendErr = extensionActionError(messageResult) || 'message_send_failed';
                // LinkedIn definitively says the recipient isn't a connection yet —
                // treat as "invite not accepted" so it retries on later ticks instead
                // of failing the lead outright.
                if (/RECIPIENT_NOT_FIRST_DEGREE_CONNECTION/i.test(sendErr)) {
                  return { success: false, reason: 'connection_not_accepted' };
                }
                return { success: false, reason: sendErr };
              }
              const sentPayload = extensionResultPayload(messageResult);
              const sentConvId = sentPayload?.conversationId;
              if (sentConvId) {
                conversationIdCacheRef.current.set(workingLead._id, String(sentConvId));
              }
              break;
            }

            case 'profile_visit': {
              const vanity = (
                workingLead.publicIdentifier
                || (isVanitySlug(workingLead.linkedinId || '') ? workingLead.linkedinId : '')
                || ''
              ).trim();
              if (!vanity) {
                return { success: false, reason: 'missing_linkedin_id' };
              }
              const visitResult = await visitLinkedInProfile(vanity);
              if (!extensionActionOk(visitResult)) {
                return { success: false, reason: 'profile_visit_failed' };
              }
              return { success: true, sentContent: 'Visited the profile' };
            }

            case 'post_like': {
              const urnResult = await resolveMemberProfileUrn(workingLead);
              if ('error' in urnResult) {
                return { success: false, reason: urnResult.error };
              }
              const rawPosts = await fetchProfilePostsCached(urnResult.urn);
              const postsError = getProfilePostsFetchError(rawPosts);
              if (postsError) {
                return { success: false, reason: postsError };
              }
              const post = extractLatestPost(rawPosts);
              if (!post) {
                return { success: false, reason: 'no_posts_found' };
              }
              const likeResult = await likeProfilePosts({
                reactionAction: 'add',
                postId: post.postId,
                ugcPostId: post.ugcPostId,
                reactionType: 'LIKE',
                newReactionType: 'LIKE',
                graphqlQueryId: process.env.REACT_APP_SOCIAL_DASH_ADD_REACTION,
                feedSignalQueryId: process.env.REACT_APP_FEED_DASH_CLIENT_SIGNAL,
              }, { all: true });
              if (!extensionActionOk(likeResult)) {
                return { success: false, reason: 'post_like_failed' };
              }
              break;
            }

            case 'post_comment': {
              const urnResult = await resolveMemberProfileUrn(workingLead);
              if ('error' in urnResult) {
                return { success: false, reason: urnResult.error };
              }
              const rawPosts = await fetchProfilePostsCached(urnResult.urn);
              const postsError = getProfilePostsFetchError(rawPosts);
              if (postsError) {
                return { success: false, reason: postsError };
              }
              const post = extractLatestPost(rawPosts);
              if (!post) {
                return { success: false, reason: 'no_posts_found' };
              }
              const commentResult = await commentProfilePosts({
                postId: post.postId,
                ugcPostId: post.ugcPostId,
                text: content,
                reactionType: 'submitComment',
                feedSignalQueryId: process.env.REACT_APP_FEED_DASH_CLIENT_SIGNAL,
              }, { all: true });
              if (!extensionActionOk(commentResult)) {
                return { success: false, reason: 'post_comment_failed' };
              }
              break;
            }
          }
          await batchDelay();
          return { success: true };
        }
      } catch (err: any) {
        console.error('[CampaignExecutor] step failed:', err?.message);
        return { success: false, reason: err?.message };
      }
    },
    [sendConnectionRequest, createOrSendMessage, likeProfilePosts, commentProfilePosts, visitLinkedInProfile,
     getLoggedInLIUser, fetchProfilePostsCached, checkExtensionStepLimit, checkConnectionRequestNote,
     resolveFirstDegree, resolveMemberProfileUrn, ensureClassicIdentity, setToast]
  );

  /**
   * Execute a step. Most failures are not toasted (visible per-lead in the sidebar).
   * Exception: LinkedIn CUSTOM_INVITE_LIMIT_REACHED gets a one-shot warning toast so the
   * user knows the note quota was hit outside LeadDelta and fallback policy will apply.
   */
  const execute = useCallback(
    (
      campaign: ICampaign,
      lead: ICampaignLead,
      step: ISequenceStep,
      content: string
    ): Promise<IStepExecutionResult> => executeInternal(campaign, lead, step, content),
    [executeInternal]
  );

  return { execute, checkLeadFirstDegree, checkLeadMessageReply, fetchLeadLatestPost };
}
